/**
 * Runs `task` one at a time. A trigger that arrives while a run is in flight
 * is remembered, not dropped: when that run settles (resolved or rejected),
 * `task` runs again if `stillNeeded()` holds, until no rerun was requested.
 * Errors from `task` are swallowed; the next trigger retries.
 */
export function serialWithRerun(
  task: () => Promise<unknown>,
  stillNeeded: () => boolean,
): () => void {
  let running = false;
  let rerun = false;

  const run = async (): Promise<void> => {
    running = true;
    try {
      do {
        rerun = false;
        try {
          await task();
        } catch {
          // Swallow: a later trigger retries.
        }
      } while (rerun && stillNeeded());
    } finally {
      running = false;
      rerun = false;
    }
  };

  return () => {
    if (running) {
      rerun = true;
      return;
    }
    void run();
  };
}
